import type { PrimaryDestination } from '@/types/city';
import type {
  ItineraryImportDraft,
  ItineraryImportSourceType,
} from '@/models/itineraryImportTypes';
import type { SourceMetadata } from '@/utils/postBuilder';

import {
  buildImportSourceMetadata,
  importAppendOverviewNotesFromDraft,
  importListNotesFromDraft,
  listCreateDestinationOptionsFromDraft,
} from '@/utils/itineraryImportBuilder';

export const IMPORT_OVERVIEW_NOTES_APPEND_DELIMITER = '\n\n---\n\n';

export interface ImportAppendToExistingOptions {
  /** Import draft `summary` (scrubbed) — appended to post overview notes when saving to existing post. */
  appendOverviewNotes?: string;
  importSourceMetadata?: SourceMetadata;
  primaryDestination?: PrimaryDestination | null;
  cityIdentifyingNames?: string[];
  /** Set when post has no existing social post id (Mongo ObjectId). */
  sourceSocialMediaPostId?: string;
}

export function mergeOverviewNotesForAppend(
  existing: string | null | undefined,
  importSummary: string | null | undefined,
): string {
  const existingTrim = existing?.trim() ?? '';
  const importTrim = importSummary?.trim() ?? '';
  if (!importTrim) return existingTrim;
  if (!existingTrim) return importTrim;
  if (existingTrim.includes(importTrim)) return existingTrim;
  return `${existingTrim}${IMPORT_OVERVIEW_NOTES_APPEND_DELIMITER}${importTrim}`;
}

export function hasExistingSourceMetadata(value: unknown): boolean {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return false;
  return Object.keys(value as Record<string, unknown>).length > 0;
}

/** Echo existing metadata or set import metadata when post had none. Never mix Inspo + import. */
export function resolveSourceMetadataForAppend(
  existing: unknown,
  importMetadata: SourceMetadata | undefined,
): SourceMetadata | Record<string, unknown> | undefined {
  if (hasExistingSourceMetadata(existing)) {
    return existing as Record<string, unknown>;
  }
  if (importMetadata) return importMetadata;
  return undefined;
}

export function buildImportAppendToExistingOptions(params: {
  draft: ItineraryImportDraft;
  sourceType: ItineraryImportSourceType;
  importId: string;
  importStartedAt: string;
}): ImportAppendToExistingOptions {
  const { primaryDestination, cityIdentifyingNames } = listCreateDestinationOptionsFromDraft(
    params.draft,
  );
  return {
    appendOverviewNotes: importAppendOverviewNotesFromDraft(params.draft, params.sourceType),
    importSourceMetadata: buildImportSourceMetadata(
      params.draft,
      params.sourceType,
      params.importId,
      params.importStartedAt,
    ),
    primaryDestination,
    cityIdentifyingNames,
  };
}
